<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { ErrorHandler } from '@/utils/errorHandler'
import ConcurrentLoginDialog from '@/components/dialogs/ConcurrentLoginDialog.vue'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const authStore = useAuthStore()
const router = useRouter()
const { t } = useI18n()

// เช็คว่าถ้า login อยู่แล้วให้ redirect ไปหน้า root
onMounted(() => {
  if (authStore.isAuthenticated)
    router.push('/')
})

// Load saved email from localStorage
const savedEmail = useLocalStorage('rememberedEmail', '')

// Default values for development only
const isDev = import.meta.env.DEV
const defaultEmail = isDev ? 'admin@tubtim.com' : ''
const defaultPassword = isDev ? 'password123' : ''

const form = ref({
  email: savedEmail.value || defaultEmail,
  password: defaultPassword,
  remember: !!savedEmail.value,
})

const isPasswordVisible = ref(false)
const error = ref<string | null>(null)
const { isLoading } = useLoading()

// Concurrent login state
const showConcurrentDialog = ref(false)
const concurrentSessionInfo = ref<any>(null)
const pendingUserId = ref<number | null>(null)

const handleLogin = async () => {
  error.value = null
  isLoading('login').value = true

  try {
    // Handle remember me functionality
    if (form.value.remember) {
      // Save email to localStorage
      savedEmail.value = form.value.email
    }
    else {
      // Clear saved email
      savedEmail.value = ''
    }

    // 1. เรียกใช้ action 'login' จาก store
    const loginResult = await authStore.login(form.value)

    // 2. ตรวจสอบว่ามี existing session หรือไม่
    if (loginResult?.hasExistingSession) {
      // แสดงข้อมูล session เก่า
      concurrentSessionInfo.value = loginResult.existingSessionInfo
      pendingUserId.value = authStore.user?.id || null
      showConcurrentDialog.value = true

      return // ไม่ redirect ไปไหน
    }
    else {
    }

    // 3. เมื่อ login สำเร็จ (ไม่มี error) ให้คอมโพเนนต์เป็นคนสั่งเปลี่ยนหน้า
    router.push('/')
  }
  catch (err) {
    // Use error handler to get user-friendly message
    error.value = ErrorHandler.handleApiError(err)
  }
  finally {
    isLoading('login').value = false
  }
}

const handleConcurrentLoginConfirm = async (userId: number) => {
  try {
    // ใช้ authStore.confirmConcurrentLogin แทนการเขียนซ้ำ
    await authStore.confirmConcurrentLogin(userId)

    // ปิด dialog และ redirect
    showConcurrentDialog.value = false
    router.push('/')
  }
  catch (err) {
    console.error('❌ Confirm login failed:', err)
    error.value = ErrorHandler.handleApiError(err)
    showConcurrentDialog.value = false
  }
}

const handleConcurrentLoginCancel = () => {
  // Logout user และ clear state
  authStore.logout()
  showConcurrentDialog.value = false
  concurrentSessionInfo.value = null
  pendingUserId.value = null
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <!-- 👉 Top shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <!-- 👉 Bottom shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <!-- 👉 Auth Card -->
      <VCard
        class="auth-card"
        max-width="460"
        :class="$vuetify.display.smAndUp ? 'pa-6' : 'pa-0'"
      >
        <VCardItem class="justify-center">
          <VCardTitle>
            <RouterLink to="/">
              <div class="app-logo">
                <VNodeRenderer :nodes="themeConfig.app.logo" />
                <h1 class="app-logo-title">
                  {{ themeConfig.app.title }}
                </h1>
              </div>
            </RouterLink>
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ t('auth.welcomeMessage') }} <span class="text-capitalize">{{ themeConfig.app.title }}</span>! 👋🏻
          </h4>
          <p class="mb-0">
            {{ t('auth.signInMessage') }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="handleLogin">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  autofocus
                  :label="t('auth.emailOrUsername')"
                  type="email"
                  placeholder="johndoe@email.com"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :label="t('auth.password')"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <!-- Error Message -->
                <VAlert
                  v-if="error"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mt-4"
                >
                  {{ error }}
                </VAlert>

                <!-- remember me checkbox -->
                <div class="d-flex align-center justify-space-between flex-wrap my-6">
                  <VCheckbox
                    v-model="form.remember"
                    :label="t('auth.rememberMe')"
                  />

                  <!--
                    <RouterLink
                    class="text-primary"
                    :to="{ name: 'pages-authentication-forgot-password-v1' }"
                    >
                    Forgot Password?
                    </RouterLink>
                  -->
                </div>

                <!-- login button -->
                <VBtn
                  block
                  type="submit"
                  :loading="isLoading('login').value"
                  :disabled="isLoading('login').value"
                >
                  {{ t('auth.login') }}
                </VBtn>
              </VCol>

              <!-- create account -->
              <VCol
                cols="12"
                class="text-body-1 text-center"
              >
                <span class="d-inline-block">
                  {{ t('auth.newOnPlatform') }}
                </span>
                <!--
                  <RouterLink
                  class="text-primary ms-1 d-inline-block text-body-1"
                  :to="{ name: 'pages-authentication-register-v1' }"
                  >
                  Create an account
                  </RouterLink>
                -->
              </VCol>

              <VCol
                cols="12"
                class="d-flex align-center"
              >
                <VDivider />
                <span class="mx-4 text-high-emphasis">{{ t('common.or') }}</span>
                <VDivider />
              </VCol>

              <!-- auth providers -->
              <VCol
                cols="12"
                class="text-center"
              >
                <AuthProvider />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>

    <!-- Concurrent Login Dialog -->
    <ConcurrentLoginDialog
      :is-visible="showConcurrentDialog"
      :session-info="concurrentSessionInfo"
      :user-id="pendingUserId"
      @update:is-visible="showConcurrentDialog = $event"
      @confirm="handleConcurrentLoginConfirm"
      @cancel="handleConcurrentLoginCancel"
    />
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
